// Начертание маркеров живого флота. Раньше суда рисовались кружками общим
// `setGeoJson` — позиция читалась, а направление нет.
//
// Словарь разведён по двум осям:
//   ФОРМА  — знаем ли курс: СПЛОШНОЙ треугольник (знаем, остриё = куда идёт) /
//            ПОЛЫЙ контур (курса нет, разворот бессмысленен);
//   ЦВЕТ   — свежесть позиции: оранжевая / серая (>24 ч, свойство `stale`).
//
// Раньше судно без курса рисовалось кружком, и флот распадался на две разные
// фигуры (фидбек владельца 2026-08-07: «серые кружочки перерисовать
// треугольниками»). Теперь силуэт один, а незнание курса показывает пустая
// заливка: сплошной значок, развёрнутый «на север», утверждал бы направление,
// которого мы не знаем, — а таких судов 1087 из 6002 на карте.
import { mapColorOf } from '../brand/mapColors'
import type { ExpressionSpecification } from 'maplibre-gl'

/** Оранжевый, а не синий: синь занята маркером косяка и треком сноса.
 *
 *  Функция, а не константа: гамма домена приезжает ответом `/cabinet/site`,
 *  позже загрузки модуля, и константа застыла бы на нашем оранжевом. */
export function vesselColor(): { fresh: string; stale: string } {
  return { fresh: mapColorOf('vessel-fresh'), stale: mapColorOf('vessel-stale') }
}

/** Идентификаторы canvas-образов в спрайте карты. */
export const VESSEL_IMAGE = {
  triFresh: 'vessel-tri-fresh',
  triStale: 'vessel-tri-stale',
  hollowFresh: 'vessel-hollow-fresh',
  hollowStale: 'vessel-hollow-stale',
} as const

/** Сторона образа, px. Треугольник при равной площади читается мельче кружка,
 *  поэтому образ крупнее прежнего радиуса 5. */
export const VESSEL_ICON_PX = 14

/** Силуэт судна: остриё вверх (север), выемка в корме — нос читается сразу.
 *
 *  Одна геометрия на карту (canvas-образ) и на легенду сайдбара (SVG). До этого
 *  легенда рисовала кружки, которых на карте давно нет, — общий силуэт не даёт
 *  им разъехаться снова. */
export function vesselSilhouette(size: number): [number, number][] {
  const mid = size / 2
  const pad = size * 0.14
  return [
    [mid, pad],                       // остриё вверх = север
    [size - pad, size - pad * 1.3],
    [mid, size - pad * 2.4],          // выемка в корме
    [pad, size - pad * 1.3],
  ]
}

/** Тот же силуэт в виде `points` для SVG-полигона легенды. */
export function vesselPolygonPoints(size: number): string {
  return vesselSilhouette(size).map(([x, y]) => `${x},${y}`).join(' ')
}

/** Курс в градусах или -1, если его нет. Ровно это значение отличает
 *  треугольник от точки, поэтому выражение одно на оба применения. */
const COURSE: ExpressionSpecification = [
  'coalesce',
  ['get', 'cog'],
  ['get', 'heading'],
  -1,
] as ExpressionSpecification

/** Образ маркера: форма от знания курса, цвет от свежести. */
export function vesselIconImage(): ExpressionSpecification {
  return [
    'case',
    ['<', COURSE, 0],
    ['case', ['get', 'stale'], VESSEL_IMAGE.hollowStale, VESSEL_IMAGE.hollowFresh],
    ['case', ['get', 'stale'], VESSEL_IMAGE.triStale, VESSEL_IMAGE.triFresh],
  ] as ExpressionSpecification
}

/** Разворот образа, градусы по часовой от севера — как ждёт `icon-rotate`.
 *
 *  `cog` (курс по грунту) приоритетнее `heading` (куда смотрит нос): при сносе
 *  течением они расходятся, а на карте нас интересует, куда судно смещается.
 *  Нет ни того, ни другого — 0, но такой маркер и так рисуется точкой. */
export function vesselIconRotate(): ExpressionSpecification {
  return ['coalesce', ['get', 'cog'], ['get', 'heading'], 0] as ExpressionSpecification
}

/** Нарисовать образ судна на canvas. Треугольник остриём ВВЕРХ (север):
 *  `icon-rotate` отсчитывает угол по часовой от севера, поэтому нулевой
 *  разворот должен смотреть вверх — иначе все курсы уехали бы на 90°.
 *
 *  `hollow` — тот же силуэт без заливки: судно на карте есть, курса у него нет.
 *  Обводка идёт цветом свежести (а не белым, как у сплошного), иначе полый
 *  значок терялся бы на светлой теме. */
export function drawVesselImage(
  ctx: CanvasRenderingContext2D,
  size: number,
  color: string,
  shape: 'triangle' | 'hollow',
): void {
  const [nose, ...tail] = vesselSilhouette(size)
  ctx.clearRect(0, 0, size, size)
  ctx.lineJoin = 'round'

  ctx.beginPath()
  ctx.moveTo(nose[0], nose[1])
  for (const [x, y] of tail) ctx.lineTo(x, y)
  ctx.closePath()

  if (shape === 'hollow') {
    ctx.strokeStyle = color
    ctx.lineWidth = size * 0.12
    ctx.stroke()
    return
  }

  ctx.fillStyle = color
  ctx.strokeStyle = '#ffffff'
  ctx.lineWidth = size * 0.07
  ctx.fill()
  ctx.stroke()
}
